import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Plus, Download, ExternalLink, FileText } from 'lucide-react'
import { getHome, getRoom, getItems, updateItem, deleteItem, downloadItemsCSV } from '../lib/supabase'
import { Modal, Field, Input, Select, Textarea, ConfirmDialog, EmptyState, Loading, DotMenu, StatusBadge, TypeBadge, formatCurrency } from '../components/ui'
import { useSettings } from '../lib/settings'
import ItemForm from './ItemForm'

function ItemCard({ item, onEdit, onDelete }) {
  const hasUrl = item.purchase_url && item.purchase_url.trim()
  const hasWarranty = item.warranty_url && item.warranty_url.trim()
  return (
    <div className="card">
      {item.image_url && (
        <div style={{ height: 140, borderRadius: '12px 12px 0 0', overflow: 'hidden' }}>
          <img src={item.image_url} alt={item.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => { e.target.parentElement.style.display = 'none' }} />
        </div>
      )}
      <DotMenu onEdit={onEdit} onDelete={onDelete} />
      <div className="card-body" style={{ paddingTop: 12, paddingRight: 40 }}>
        <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '.9375rem', letterSpacing: '-.01em', marginBottom: 3, lineHeight: 1.3 }}>{item.name}</h3>
        {item.brand && <div style={{ fontSize: '.75rem', color: 'var(--ocean)', marginBottom: 8 }}>{item.brand}</div>}
        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 8 }}>
          <StatusBadge status={item.status} />
          {item.type && <TypeBadge type={item.type} />}
        </div>
        {item.details && <p style={{ fontSize: '.75rem', color: 'var(--text-secondary)', marginBottom: 8, lineHeight: 1.5 }}>{item.details}</p>}
        {item.model_number && <div style={{ fontSize: '.72rem', color: 'var(--ocean)', fontFamily: 'var(--font-mono)', marginBottom: 6 }}>{item.model_number}</div>}
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginTop: 6 }}>
          {hasUrl && (
            <a href={item.purchase_url} target="_blank" rel="noopener noreferrer"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '.75rem', fontWeight: 500, color: 'var(--navy)', background: 'var(--navy-light)', padding: '4px 10px', borderRadius: 99, textDecoration: 'none' }}
              onClick={e => e.stopPropagation()}>
              <ExternalLink size={11} /> View item
            </a>
          )}
          {hasWarranty && (
            <a href={item.warranty_url} target="_blank" rel="noopener noreferrer"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '.75rem', color: 'var(--ocean)' }}
              onClick={e => e.stopPropagation()}>
              <FileText size={11} /> Warranty
            </a>
          )}
        </div>
      </div>
    </div>
  )
}

export default function RoomDetailPage() {
  const { homeId, roomId } = useParams()
  const { activeCategories, showRoomTotals } = useSettings()
  const [home, setHome] = useState(null)
  const [room, setRoom] = useState(null)
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('All')
  const [modal, setModal] = useState(null)
  const [deleting, setDeleting] = useState(null)
  const [deleteLoading, setDeleteLoading] = useState(false)

  const load = async () => {
    try {
      const [h, r, it] = await Promise.all([getHome(homeId), getRoom(roomId), getItems(roomId)])
      setHome(h); setRoom(r); setItems(it)
    } finally { setLoading(false) }
  }
  useEffect(() => { load() }, [roomId])

  const handleDelete = async () => {
    setDeleteLoading(true)
    try { await deleteItem(deleting.id); setDeleting(null); load() }
    finally { setDeleteLoading(false) }
  }

  if (loading) return <div className="page-body"><Loading /></div>
  if (!room) return <div className="page-body"><p>Space not found.</p></div>

  const tabs = ['All', ...activeCategories.map(c => c.key)]
  const filtered = activeTab === 'All' ? items : items.filter(i => i.type === activeTab)
  const total = items.reduce((s, i) => s + (i.cost || 0), 0)
  const open = items.filter(i => i.status === 'Considering' || i.status === 'Shortlisted').length

  return (
    <div className="page-body">
      <div className="breadcrumb">
        <Link to="/">Properties</Link>
        <span className="breadcrumb-sep">›</span>
        <Link to={`/homes/${homeId}`}>{home?.name}</Link>
        <span className="breadcrumb-sep">›</span>
        <span>{room.name}</span>
      </div>

      <div className="page-header">
        <div className="page-header-left">
          <h1 className="page-title">{room.name}</h1>
          <p className="page-subtitle">{[room.room_type, room.floor_level].filter(Boolean).join(' · ')}</p>
        </div>
        <div className="page-header-actions">
          <button className="btn btn-ghost btn-sm" onClick={() => downloadItemsCSV(items, [], room.name.replace(/\s+/g, '_') + '_items')}>
            <Download size={13} /> CSV
          </button>
          <button className="btn btn-primary" onClick={() => setModal({ type: 'add' })}>
            <Plus size={15} /> Add Item
          </button>
        </div>
      </div>

      {items.length > 0 && (
        <div className="stats-row">
          <div className="stat-item"><span className="stat-value">{items.length}</span><span className="stat-label">Items</span></div>
          {showRoomTotals && total > 0 && <div className="stat-item"><span className="stat-value">{formatCurrency(total)}</span><span className="stat-label">Total Est.</span></div>}
          <div className="stat-item"><span className="stat-value">{open}</span><span className="stat-label">Open Decisions</span></div>
        </div>
      )}

      <div className="tab-bar">
        {tabs.map(tab => (
          <button key={tab} className={`tab-btn${activeTab === tab ? ' active' : ''}`} onClick={() => setActiveTab(tab)}>
            {tab}
            {tab !== 'All' && <span style={{ marginLeft: 5, fontSize: '.65rem', opacity: .6 }}>{items.filter(i => i.type === tab).length}</span>}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <EmptyState icon="📦" title={activeTab === 'All' ? 'No items yet' : `No ${activeTab} items`}
          description="Add finishes, appliances, furniture, or decor to track selections for this space."
          action={<button className="btn btn-primary" onClick={() => setModal({ type: 'add' })}><Plus size={15} /> Add Item</button>} />
      ) : (
        <div className="grid-items">
          {filtered.map(item => (
            <ItemCard key={item.id} item={item}
              onEdit={() => setModal({ type: 'edit', item })}
              onDelete={() => setDeleting(item)} />
          ))}
        </div>
      )}

      {modal?.type === 'add' && <ItemForm roomId={roomId} homeId={homeId} onSave={() => { setModal(null); load() }} onClose={() => setModal(null)} />}
      {modal?.type === 'edit' && <ItemForm initial={modal.item} roomId={roomId} homeId={homeId} onSave={() => { setModal(null); load() }} onClose={() => setModal(null)} />}
      {deleting && <ConfirmDialog title="Delete Item" message={`Delete "${deleting.name}"? This cannot be undone.`} onConfirm={handleDelete} onCancel={() => setDeleting(null)} loading={deleteLoading} />}
    </div>
  )
}
